import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTimes } from 'react-icons/fa';
import API_BASE_URL from '../apiconfig/api-config';
import { formatDateDDMMYYEmpty } from '../common/dateUtils';

export default function DocumentUploadForm() {
  const [form, setForm] = useState({
    order_id: "",
    bl_id: "",
    file_reference_no: "",
  });

  const [documentFields, setDocumentFields] = useState([
    { doc_type_id: "", file: null }
  ]);

  const [orders, setOrders] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch dropdown data from backend
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.data)) {
          setOrders(data.data);
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
      }
    };

    const fetchDocTypes = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-document-types`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.data)) {
          setDocTypes(data.data);
        }
      } catch (err) {
        console.error('Error fetching document types:', err);
      }
    };

    fetchOrders();
    fetchDocTypes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDocumentFieldChange = (index, e) => {
    const { name, value, files } = e.target;
    const updatedFields = [...documentFields];
    
    if (files) {
      updatedFields[index][name] = files[0];
    } else {
      updatedFields[index][name] = value;
    }
    
    setDocumentFields(updatedFields);
  };

  const addDocumentField = () => {
    setDocumentFields([...documentFields, { doc_type_id: "", file: null }]);
  };

  const removeDocumentField = (index) => {
    if (documentFields.length > 1) {
      const updatedFields = [...documentFields];
      updatedFields.splice(index, 1);
      setDocumentFields(updatedFields);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    
    try {
      // Validate at least one document is selected
      const hasEmptyFields = documentFields.some(
        field => !field.doc_type_id || !field.file
      );
      
      if (hasEmptyFields) {
        setMessage({ type: 'error', text: 'Please fill all document fields' });
        setLoading(false);
        return;
      }

      // Prepare FormData for each document
      const uploadPromises = documentFields.map(field => {
        const formData = new FormData();
        Object.entries(form).forEach(([key, value]) => {
          if (value !== null && value !== "") {
            formData.append(key, value);
          }
        });
        formData.append('doc_type_id', field.doc_type_id);
        formData.append('file', field.file);

        return fetch(`${API_BASE_URL}/api/document/upload-document`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
      });

      // Execute all uploads in parallel
      const results = await Promise.all(uploadPromises);
      const allSuccessful = results.every(res => res.ok);

      if (allSuccessful) {
        setMessage({ type: 'success', text: 'All documents uploaded successfully!' });
        setForm({
          order_id: "",
          bl_id: "",
          file_reference_no: "",
        });
        setDocumentFields([{ doc_type_id: "", file: null }]);
        setTimeout(() => navigate('/document'), 1200);
      } else {
        setMessage({ type: 'error', text: 'Some documents failed to upload' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server error' });
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Document Upload</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          {message && (
            <div className={`mb-4 p-3 rounded ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {message.text}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Order</label>
            <select
              name="order_id"
              value={form.order_id}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={loading}
            >
              <option value="">-- Select Order --</option>
              {orders.map((order) => (
                <option key={order.order_id} value={order.order_id}>
                  {order.order_id} - {order.purchase_contract_no} ({order.order_type}) - {formatDateDDMMYYEmpty(order.order_date) || 'No Date'} - {order.quantity_mt ? `${order.quantity_mt} MT` : 'No Qty'} - {order.status || 'No Status'}
                </option>
              ))}
            </select>
          </div>

          {documentFields.map((field, index) => (
            <div key={index} className="mb-4 p-3 border border-gray-200 rounded-lg relative">
              {documentFields.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeDocumentField(index)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  disabled={loading}
                >
                  <FaTimes />
                </button>
              )}
              
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Document Type</label>
                <select
                  name="doc_type_id"
                  value={field.doc_type_id}
                  onChange={(e) => handleDocumentFieldChange(index, e)}
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loading}
                >
                  <option value="">-- Select Document Type --</option>
                  {docTypes.map((doc) => (
                    <option key={doc.doc_type_id} value={doc.doc_type_id}>
                      {doc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Document File</label>
                <input
                  type="file"
                  name="file"
                  onChange={(e) => handleDocumentFieldChange(index, e)}
                  required
                  className="mt-2 block w-full text-sm text-gray-600
                           file:mr-4 file:py-2 file:px-4
                           file:rounded-lg file:border-0
                           file:text-sm file:font-semibold
                           file:bg-blue-50 file:text-blue-700
                           hover:file:bg-blue-100"
                  disabled={loading}
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addDocumentField}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium mb-4"
            disabled={loading}
          >
            <FaPlus /> Add Another Document
          </button>
        </div>
        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#10a5e9] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Uploading...' : 'Upload Documents'}
          </button>
        </div>
      </form>
    </div>
  );
}